/**
 * Applicant-facing Checkr Tenant screening projection. Shared by the public BFF
 * (which sanitizes the backend status) and the continuation page (which
 * re-checks before opening the provider's secure tab).
 */
export type PublicScreeningState = 'action_required' | 'waiting_on_provider' | 'in_progress' | 'completed' | 'needs_follow_up';
export type PublicScreeningProgress = 'not_started' | 'visited' | 'started' | 'completed' | 'paid';

export interface PublicScreening {
  state: PublicScreeningState;
  payer: 'applicant' | 'organization';
  feeCents: number | null;
  applicantProgress: PublicScreeningProgress;
  providerName: 'Checkr';
  applicationUrl: string | null;
  isTest: boolean;
}

const STATES: ReadonlySet<string> = new Set(['action_required', 'waiting_on_provider', 'in_progress', 'completed', 'needs_follow_up']);
const PROGRESS: ReadonlySet<string> = new Set(['not_started', 'visited', 'started', 'completed', 'paid']);
export const CHECKR_APPLICANT_HOST = 'tenant.checkr.com';

/**
 * Only an HTTPS URL on Checkr's applicant host may be opened. Anything else,
 * including credentials, custom ports or lookalike hosts, is discarded.
 */
export function safeCheckrApplicationUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value.length > 2048) return null;
  let url: URL;
  try { url = new URL(value); } catch { return null; }
  if (url.protocol !== 'https:' || url.hostname !== CHECKR_APPLICANT_HOST || url.port || url.username || url.password) return null;
  return url.href;
}

/** Returns a safe projection, or null when screening is absent or malformed. */
export function safePublicScreening(value: unknown): PublicScreening | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  if (typeof row.state !== 'string' || !STATES.has(row.state)) return null;
  if (row.payer !== 'applicant' && row.payer !== 'organization') return null;
  const feeCents = typeof row.feeCents === 'number' && Number.isSafeInteger(row.feeCents) && row.feeCents >= 0 ? row.feeCents : null;
  const applicantProgress = typeof row.applicantProgress === 'string' && PROGRESS.has(row.applicantProgress)
    ? row.applicantProgress as PublicScreeningProgress
    : 'not_started';
  return {
    state: row.state as PublicScreeningState,
    payer: row.payer,
    feeCents,
    applicantProgress,
    providerName: 'Checkr',
    // The secure link is only meaningful while the applicant has to act.
    applicationUrl: row.state === 'action_required' ? safeCheckrApplicationUrl(row.applicationUrl) : null,
    isTest: row.isTest === true,
  };
}

/** Screening states that still change while the applicant waits on this page. */
export function screeningNeedsRefresh(screening: PublicScreening | null | undefined): boolean {
  return !!screening && (screening.state === 'action_required' || screening.state === 'waiting_on_provider' || screening.state === 'in_progress');
}

export const PUBLIC_SCREENING_POLL_MS = 15_000;
